import { useState, useCallback, useRef } from "react";
import * as XLSX from "xlsx";

/* ═══════════════════════════════════════════════════════════════
   OSRS COMBAT FORMULA & SKILLS
   ═══════════════════════════════════════════════════════════════ */
function calcCombatLevel(sk) { 
  const d=sk.defence?.level??1,h=sk.hitpoints?.level??10,p=sk.prayer?.level??1;
  const a=sk.attack?.level??1,s=sk.strength?.level??1,r=sk.ranged?.level??1,m=sk.magic?.level??1;
  const base=0.25*(d+h+Math.floor(p/2));
  return Math.floor(base+Math.max(0.325*(a+s),0.325*Math.floor(3*r/2),0.325*Math.floor(3*m/2)));
} 
 
function getCombatStyle(sk) {
  const a=sk.attack?.level??1,s=sk.strength?.level??1,r=sk.ranged?.level??1,m=sk.magic?.level??1;
  const mel=0.325*(a+s),rng=0.325*Math.floor(3*r/2),mag=0.325*Math.floor(3*m/2);
  const b=Math.max(mel,rng,mag);
  return b===mel?"Melee":b===rng?"Ranged":"Magic";
}

// OSRS HiScores CSV skill order (each line = rank,level,xp)
const HISCORE_SKILL_ORDER = [
  "overall","attack","defence","strength","hitpoints","ranged","prayer","magic",
  "cooking","woodcutting","fletching","fishing","firemaking","crafting",
  "smithing","mining","herblore","agility","thieving","slayer","farming",
  "runecrafting","hunter","construction","sailing"
];

const ALL_SKILLS = HISCORE_SKILL_ORDER.filter(s => s !== "overall" && s !== "sailing");
const COMBAT_SKILLS = ["attack","strength","defence","hitpoints","ranged","prayer","magic"];
const NON_COMBAT = ALL_SKILLS.filter(s => !COMBAT_SKILLS.includes(s));
const prettify = s => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g," ");
const wait = ms => new Promise(r => setTimeout(r, ms));

/* ═══════════════════════════════════════════════════════════════
   WOM API — member list only (1 call)
   ═══════════════════════════════════════════════════════════════ */
const WOM = "https://api.wiseoldman.net/v2";

async function womFetch(url) {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`WOM error ${r.status}`);
  return r.json();
}

async function searchGroups(name) {
  return await womFetch(`${WOM}/groups?name=${encodeURIComponent(name)}&limit=10`);
}

async function getGroupDetails(id) {
  return await womFetch(`${WOM}/groups/${id}`);
}

async function getFullMemberList(id, onMsg) {
  // Paginate through ALL members — WOM defaults to 20 per page
  const PAGE_SIZE = 50;
  let all = [];
  let offset = 0;
  let page = 0;

  while (true) {
    page++;
    if (onMsg) onMsg(`Fetching member list (page ${page}, ${all.length} found so far)...`);
    const url = `${WOM}/groups/${id}/hiscores?metric=overall&limit=${PAGE_SIZE}&offset=${offset}`;
    const r = await womFetch(url);
    if (!Array.isArray(r) || r.length === 0) break;
    all = all.concat(r);
    if (r.length < PAGE_SIZE) break; // last page
    offset += PAGE_SIZE;
    await wait(1200); // small delay between pages to be respectful
  }

  if (all.length === 0) throw new Error("No members found");

  // Deduplicate by player id just in case
  const seen = new Set();
  return all.map(e => ({
    username: e.player?.username,
    displayName: e.player?.displayName,
    id: e.player?.id,
    overallLevel: e.data?.level ?? 0,
    overallXp: e.data?.experience ?? 0,
    overallRank: e.data?.rank ?? -1,
    combatLevel: e.player?.combatLevel ?? null,
  })).filter(m => {
    if (!m.username || seen.has(m.id)) return false;
    seen.add(m.id);
    return true;
  });
}

/* ═══════════════════════════════════════════════════════════════
   OSRS HISCORES — direct from Jagex via CORS proxy
   ═══════════════════════════════════════════════════════════════ */
const JAGEX_HISCORES = "https://secure.runescape.com/m=hiscore_oldschool/index_lite.ws";

// Multiple CORS proxies for fallback reliability
const CORS_PROXIES = [
  url => `https://corsproxy.io/?url=${encodeURIComponent(url)}`,
  url => `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
  url => `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`,
];

let workingProxyIdx = 0; // remember which proxy works

async function fetchWithProxy(url, timeoutMs = 10000) {
  // Try the last known working proxy first, then cycle through others
  const order = [];
  for (let i = 0; i < CORS_PROXIES.length; i++) {
    order.push((workingProxyIdx + i) % CORS_PROXIES.length);
  }

  for (const idx of order) {
    const proxied = CORS_PROXIES[idx](url);
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      const r = await fetch(proxied, { signal: controller.signal });
      clearTimeout(timer);
      if (r.ok) {
        const text = await r.text();
        if (text && text.includes(",") && !text.includes("<html")) {
          workingProxyIdx = idx; // remember this one works
          return text;
        }
      }
    } catch (e) {
      // try next proxy
    }
  }
  return null;
}

function parseHiscoresCsv(csv) {
  if (!csv) return null;
  const lines = csv.trim().split("\n");
  const skills = {};

  for (let i = 0; i < HISCORE_SKILL_ORDER.length && i < lines.length; i++) {
    const parts = lines[i].trim().split(",");
    if (parts.length >= 3) {
      const rank = parseInt(parts[0], 10);
      const level = parseInt(parts[1], 10);
      const xp = parseInt(parts[2], 10);
      skills[HISCORE_SKILL_ORDER[i]] = {
        rank: isNaN(rank) ? -1 : rank,
        level: isNaN(level) ? 1 : level,
        experience: isNaN(xp) ? 0 : xp,
      };
    }
  }
  return skills;
}

async function fetchPlayerHiscores(username) {
  const url = `${JAGEX_HISCORES}?player=${encodeURIComponent(username)}`;
  const csv = await fetchWithProxy(url);
  return parseHiscoresCsv(csv);
}

/* ═══════════════════════════════════════════════════════════════
   MEMBER OBJECTS
   ═══════════════════════════════════════════════════════════════ */
function makePlaceholder(m) {
  return {
    player: { id: m.id, username: m.username, displayName: m.displayName, combatLevel: m.combatLevel },
    data: { skills: { overall: { level: m.overallLevel, experience: m.overallXp, rank: m.overallRank } } },
    loaded: false, failed: false,
  };
}

function makeLoaded(placeholder, hiscoreSkills) {
  return {
    ...placeholder,
    data: { skills: hiscoreSkills },
    loaded: true, failed: false,
  };
}

function makeFailed(placeholder) {
  return { ...placeholder, loaded: true, failed: true };
}

/* ═══════════════════════════════════════════════════════════════
   SCAN ENGINE — fast iteration over OSRS HiScores
   ═══════════════════════════════════════════════════════════════ */
async function scanAllMembers(memberList, onUpdate, onMsg, cancelRef) {
  const results = memberList.map(m => makePlaceholder(m));
  onUpdate([...results], 0, 0);

  let loaded = 0, failed = 0;

  for (let i = 0; i < memberList.length; i++) {
    if (cancelRef.current) return results;

    const m = memberList[i];
    onMsg(`Scanning ${m.displayName || m.username} (${i + 1}/${memberList.length})`);

    const skills = await fetchPlayerHiscores(m.username);

    if (skills && Object.keys(skills).length > 0) {
      results[i] = makeLoaded(results[i], skills);
      loaded++;
    } else {
      results[i] = makeFailed(results[i]);
      failed++;
    }

    onUpdate([...results], loaded, failed);

    // Small delay to be respectful — 600ms between requests
    if (i < memberList.length - 1 && !cancelRef.current) {
      await wait(600);
    }
  }

  return results;
}

/* ═══════════════════════════════════════════════════════════════
   XLSX EXPORT
   ═══════════════════════════════════════════════════════════════ */
function exportToXlsx(members, groupName) {
  const loaded = members.filter(m => m.loaded && !m.failed);
  const h1 = ["Rank","Player","Combat Lvl","Combat Style","Total Level",...ALL_SKILLS.map(prettify),"Overall XP"];
  const rows = loaded.map((m, i) => {
    const sk = m.data?.skills || {};
    const cmb = m.player?.combatLevel ?? calcCombatLevel(sk);
    return [i+1, m.player?.displayName ?? "Unknown", cmb, getCombatStyle(sk), sk.overall?.level ?? 0,
      ...ALL_SKILLS.map(s => sk[s]?.level ?? 0), sk.overall?.experience ?? 0];
  });
  const ws = XLSX.utils.aoa_to_sheet([h1, ...rows]);
  ws["!cols"] = h1.map((_, i) => ({ wch: i === 1 ? 18 : i <= 4 ? 14 : 12 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Clan Stats");

  const xH = ["Player", ...ALL_SKILLS.map(s => `${prettify(s)} XP`), "Overall XP"];
  const xR = loaded.map(m => { const sk = m.data?.skills || {}; return [m.player?.displayName ?? "Unknown", ...ALL_SKILLS.map(s => sk[s]?.experience ?? 0), sk.overall?.experience ?? 0]; });
  const ws2 = XLSX.utils.aoa_to_sheet([xH, ...xR]);
  ws2["!cols"] = xH.map((_, i) => ({ wch: i === 0 ? 18 : 14 }));
  XLSX.utils.book_append_sheet(wb, ws2, "Experience");

  const rH = ["Player", ...ALL_SKILLS.map(s => `${prettify(s)} Rank`), "Overall Rank"];
  const rR = loaded.map(m => { const sk = m.data?.skills || {}; return [m.player?.displayName ?? "Unknown", ...ALL_SKILLS.map(s => sk[s]?.rank > 0 ? sk[s].rank : "Unranked"), sk.overall?.rank > 0 ? sk.overall.rank : "Unranked"]; });
  const ws3 = XLSX.utils.aoa_to_sheet([rH, ...rR]);
  ws3["!cols"] = rH.map((_, i) => ({ wch: i === 0 ? 18 : 14 }));
  XLSX.utils.book_append_sheet(wb, ws3, "Ranks");

  const safe = (groupName || "clan").replace(/[^a-zA-Z0-9 ]/g, "").trim().replace(/\s+/g, "_");
  XLSX.writeFile(wb, `${safe}_stats.xlsx`);
}

/* ═══════════════════════════════════════════════════════════════
   THEME
   ═══════════════════════════════════════════════════════════════ */
const T = { bg:"#060a12",bgCard:"#0a1020",bgHover:"rgba(0,180,255,0.04)",bgRow1:"rgba(8,16,32,0.7)",bgRow2:"rgba(12,22,42,0.7)",accent:"#00c8ff",accentDim:"#0080aa",accentGlow:"rgba(0,200,255,0.15)",text:"#c8dce8",textBright:"#e4f0f8",textDim:"#4a6278",textMuted:"#2a3e52",border:"rgba(0,180,255,0.1)",borderBright:"rgba(0,200,255,0.25)",melee:"#ff4455",ranged:"#44dd66",magic:"#44aaff",green:"#00e88a",panel:"rgba(0,180,255,0.03)",warn:"#ffaa33",red:"#ff4466" };

/* ═══════════════════════════════════════════════════════════════
   COMPONENTS
   ═══════════════════════════════════════════════════════════════ */
function StatBadge({ label, value, color = T.accent }) {
  return (<div style={{ display:"flex",flexDirection:"column",alignItems:"center",padding:"10px 16px",background:T.bgCard,borderRadius:8,border:`1px solid ${T.border}`,minWidth:70 }}>
    <span style={{ fontSize:9,color:T.textDim,textTransform:"uppercase",letterSpacing:1.5,marginBottom:4 }}>{label}</span>
    <span style={{ fontSize:20,fontWeight:800,color,fontFamily:"'Rajdhani',sans-serif" }}>{value}</span>
  </div>);
}

function FeatureCard({ icon, title, desc }) {
  return (<div style={{ padding:"32px 26px",background:T.bgCard,borderRadius:14,border:`1px solid ${T.border}`,transition:"all 0.3s",cursor:"default" }}
    onMouseEnter={e => { e.currentTarget.style.borderColor=T.borderBright; e.currentTarget.style.boxShadow=`0 0 30px ${T.accentGlow}`; e.currentTarget.style.transform="translateY(-2px)"; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor=T.border; e.currentTarget.style.boxShadow="none"; e.currentTarget.style.transform="translateY(0)"; }}>
    <div style={{ fontSize:30,marginBottom:16 }}>{icon}</div>
    <div style={{ fontSize:16,fontWeight:700,color:T.textBright,fontFamily:"'Rajdhani',sans-serif",letterSpacing:1,marginBottom:8 }}>{title}</div>
    <div style={{ fontSize:12.5,color:T.textDim,lineHeight:1.7 }}>{desc}</div>
  </div>);
}

function PlayerRow({ member, index, expanded, onToggle }) {
  const sk = member.data?.skills || {};
  const loaded = member.loaded;
  const failed = member.failed;
  const combat = loaded && !failed ? (member.player?.combatLevel ?? calcCombatLevel(sk)) : (member.player?.combatLevel ?? null);
  const style = loaded && !failed ? getCombatStyle(sk) : null;
  const name = member.player?.displayName ?? member.player?.username ?? "Unknown";
  const styleColor = style === "Melee" ? T.melee : style === "Ranged" ? T.ranged : T.magic;
  const total = sk.overall?.level ?? 0;
  const xp = sk.overall?.experience ?? 0;
  const pct = combat ? Math.min(combat / 126 * 100, 100) : 0;

  return (<div style={{ marginBottom:1 }}>
    <div onClick={onToggle} style={{
      display:"grid", gridTemplateColumns:"36px 1.4fr 0.9fr 0.7fr 0.7fr 1fr",
      alignItems:"center", padding:"11px 16px",
      background: expanded ? T.bgCard : index % 2 === 0 ? T.bgRow1 : T.bgRow2,
      cursor:"pointer", transition:"background 0.15s",
      borderLeft:`3px solid ${expanded ? T.accent : "transparent"}`,
      opacity: loaded ? 1 : 0.45,
    }}
      onMouseEnter={e => { if (!expanded) e.currentTarget.style.background = T.bgHover; }}
      onMouseLeave={e => { if (!expanded) e.currentTarget.style.background = index % 2 === 0 ? T.bgRow1 : T.bgRow2; }}>
      <span style={{ color:T.textMuted,fontWeight:600,fontSize:12 }}>#{index + 1}</span>
      <div style={{ display:"flex",alignItems:"center",gap:6 }}>
        <span style={{ color:T.textBright,fontWeight:700,fontSize:13,fontFamily:"'Rajdhani',sans-serif" }}>{name}</span>
        {!loaded && <span style={{ fontSize:8,color:T.warn,background:"rgba(255,170,51,0.1)",padding:"1px 6px",borderRadius:3 }}>PENDING</span>}
        {failed && <span style={{ fontSize:8,color:T.red,background:"rgba(255,68,102,0.1)",padding:"1px 6px",borderRadius:3 }}>NOT ON HISCORES</span>}
      </div>
      <div style={{ display:"flex",alignItems:"center",gap:8 }}>
        <div style={{ flex:1,height:4,background:"rgba(0,200,255,0.08)",borderRadius:2,overflow:"hidden" }}>
          <div style={{ width:`${pct}%`,height:"100%",background:`linear-gradient(90deg,${T.accentDim},${T.accent})`,borderRadius:2,transition:"width 0.5s" }} />
        </div>
        <span style={{ color:combat ? T.accent : T.textDim,fontWeight:800,fontSize:15,fontFamily:"'Rajdhani',sans-serif",minWidth:28,textAlign:"right" }}>{combat ?? "—"}</span>
      </div>
      <span style={{ color: style ? styleColor : T.textMuted,fontSize:11,fontWeight:700,textAlign:"center",textTransform:"uppercase",letterSpacing:1.2 }}>{style ?? "—"}</span>
      <span style={{ color:T.text,fontSize:12,textAlign:"center",fontFamily:"'Rajdhani',sans-serif",fontWeight:600 }}>{total}</span>
      <span style={{ color:T.textDim,fontSize:11,textAlign:"right",fontFamily:"'Rajdhani',sans-serif" }}>{xp.toLocaleString()} xp</span>
    </div>

    {expanded && (<div style={{ padding:"20px 24px",background:"rgba(4,10,22,0.85)",borderLeft:`3px solid ${T.accent}`,borderBottom:`1px solid ${T.border}` }}>
      {!loaded ? (
        <div style={{ padding:24,textAlign:"center",color:T.textDim,fontSize:12 }}>
          <div style={{ width:20,height:20,border:`2px solid ${T.border}`,borderTop:`2px solid ${T.accent}`,borderRadius:"50%",margin:"0 auto 10px",animation:"spin 0.7s linear infinite" }} />
          Waiting for this player's data...
        </div>
      ) : failed ? (
        <div style={{ padding:24,textAlign:"center",color:T.textDim,fontSize:12 }}>
          This player couldn't be found on the OSRS HiScores. They may be unranked or have a name change pending on WOM.
        </div>
      ) : (
        <>
          <div style={{ display:"flex",gap:10,flexWrap:"wrap",marginBottom:18 }}>
            <StatBadge label="Combat" value={combat} />
            <StatBadge label="Total" value={total} />
            <StatBadge label="Overall XP" value={(xp / 1000000).toFixed(1) + "M"} color={T.green} />
          </div>
          <div style={{ fontSize:9,color:T.textMuted,textTransform:"uppercase",letterSpacing:2,marginBottom:8,fontWeight:700 }}>Combat Skills</div>
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:6,marginBottom:16 }}>
            {COMBAT_SKILLS.map(s => { const lv = sk[s]?.level ?? 1; const p2 = Math.min(lv / 99 * 100, 100); return (
              <div key={s} style={{ padding:"8px 10px",background:"rgba(0,200,255,0.03)",borderRadius:6,border:`1px solid ${T.border}` }}>
                <div style={{ display:"flex",justifyContent:"space-between",marginBottom:4 }}>
                  <span style={{ color:T.textDim,fontSize:10 }}>{prettify(s)}</span>
                  <span style={{ color:T.textBright,fontSize:12,fontWeight:700,fontFamily:"'Rajdhani',sans-serif" }}>{lv}</span>
                </div>
                <div style={{ height:2,background:"rgba(0,200,255,0.08)",borderRadius:1 }}>
                  <div style={{ width:`${p2}%`,height:"100%",background:T.accent,borderRadius:1 }} />
                </div>
              </div>); })}
          </div>
          <div style={{ fontSize:9,color:T.textMuted,textTransform:"uppercase",letterSpacing:2,marginBottom:8,fontWeight:700 }}>Other Skills</div>
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:5 }}>
            {NON_COMBAT.map(s => (<div key={s} style={{ padding:"6px 10px",background:"rgba(0,180,255,0.015)",borderRadius:4,border:"1px solid rgba(0,180,255,0.05)",display:"flex",justifyContent:"space-between",alignItems:"center" }}>
              <span style={{ color:T.textMuted,fontSize:10 }}>{prettify(s)}</span>
              <span style={{ color:T.text,fontSize:11,fontWeight:600,fontFamily:"'Rajdhani',sans-serif" }}>{sk[s]?.level ?? 1}</span>
            </div>))}
          </div>
        </>
      )}
    </div>)}
  </div>);
}

function Particles() {
  const pts = Array.from({ length: 35 }, (_, i) => ({ id:i, x:Math.random()*100, y:Math.random()*100, sz:Math.random()*2+0.5, dur:Math.random()*20+15, del:Math.random()*10, op:Math.random()*0.25+0.03 }));
  return (<div style={{ position:"fixed",inset:0,pointerEvents:"none",zIndex:0,overflow:"hidden" }}>
    {pts.map(p => <div key={p.id} style={{ position:"absolute",left:`${p.x}%`,top:`${p.y}%`,width:p.sz,height:p.sz,borderRadius:"50%",background:T.accent,opacity:p.op,animation:`pFloat ${p.dur}s ease-in-out ${p.del}s infinite alternate` }} />)}
  </div>);
}

/* ═══════════════════════════════════════════════════════════════
   MAIN APP
   ═══════════════════════════════════════════════════════════════ */
export default function App() {
  const [view, setView] = useState("landing");
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [loadedCount, setLoadedCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [sortBy, setSortBy] = useState("combat");
  const [filterText, setFilterText] = useState("");
  const [allDone, setAllDone] = useState(false);
  const cancelRef = useRef(false);

  const doLoad = async (groupId) => {
    cancelRef.current = false;
    setAllDone(false); setLoadedCount(0); setFailedCount(0); setTotalCount(0);

    // STEP 1: Get complete member list from WOM (paginated — fetches ALL members)
    setStatusMsg("Fetching member list from Wise Old Man...");
    const memberList = await getFullMemberList(groupId, setStatusMsg);
    setTotalCount(memberList.length);
    setStatusMsg(`Found ${memberList.length} members. Testing CORS proxy...`);

    // Show table with placeholders immediately
    const placeholders = memberList.map(m => makePlaceholder(m));
    setMembers(placeholders);
    setInitialLoading(false);

    // STEP 2: Fetch each player from OSRS HiScores (fast — ~0.6s per player)
    setStatusMsg(`Scanning ${memberList.length} players via OSRS HiScores...`);
    await scanAllMembers(
      memberList,
      (updated, loaded, failed) => { setMembers(updated); setLoadedCount(loaded); setFailedCount(failed); },
      (msg) => { setStatusMsg(msg); },
      cancelRef
    );

    if (!cancelRef.current) {
      setAllDone(true);
      setStatusMsg("");
    }
  };

  const handleSearch = useCallback(async (oq) => {
    const q = (oq ?? query).trim();
    if (!q) return;
    cancelRef.current = true;
    await wait(200);
    cancelRef.current = false;
    setError(null); setSearchResults(null); setSelectedGroup(null); setMembers([]);
    setInitialLoading(true); setLoadedCount(0); setFailedCount(0); setTotalCount(0); setAllDone(false); setStatusMsg("");
    if (view === "landing") setView("app");
    try {
      if (/^\d+$/.test(q)) {
        const g = await getGroupDetails(q);
        setSelectedGroup(g);
        await doLoad(g.id);
      } else {
        const res = await searchGroups(q);
        if (!res.length) { setError("No groups found. Try a different name or enter a Group ID."); setInitialLoading(false); }
        else if (res.length === 1) { setSelectedGroup(res[0]); await doLoad(res[0].id); }
        else { setSearchResults(res); setInitialLoading(false); }
      }
    } catch (e) { setError(e.message); setInitialLoading(false); }
  }, [query, view]);

  const selectGroup = async (g) => {
    cancelRef.current = true; await wait(200);
    setSearchResults(null); setSelectedGroup(g); setInitialLoading(true); setError(null);
    setLoadedCount(0); setFailedCount(0); setTotalCount(0); setAllDone(false);
    try { await doLoad(g.id); } catch (e) { setError(e.message); setInitialLoading(false); }
  };

  const sorted = [...members].sort((a, b) => {
    const sA = a.data?.skills || {}, sB = b.data?.skills || {};
    if (sortBy === "combat") {
      const cA = a.loaded && !a.failed ? (a.player?.combatLevel ?? calcCombatLevel(sA)) : 0;
      const cB = b.loaded && !b.failed ? (b.player?.combatLevel ?? calcCombatLevel(sB)) : 0;
      return cB - cA;
    }
    if (sortBy === "total") return (sB.overall?.level ?? 0) - (sA.overall?.level ?? 0);
    if (sortBy === "xp") return (sB.overall?.experience ?? 0) - (sA.overall?.experience ?? 0);
    if (sortBy === "name") return (a.player?.displayName ?? "").localeCompare(b.player?.displayName ?? "");
    return 0;
  }).filter(m => !filterText || (m.player?.displayName ?? m.player?.username ?? "").toLowerCase().includes(filterText.toLowerCase()));

  const pct = totalCount > 0 ? Math.round((loadedCount + failedCount) / totalCount * 100) : 0;
  const isScanning = totalCount > 0 && !allDone && !initialLoading;
  const processed = loadedCount + failedCount;
  const remaining = totalCount - processed;
  const etaSec = Math.ceil(remaining * 0.7); // ~0.7s per player

  const CSS = `
    @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&family=Orbitron:wght@400;500;600;700;800;900&display=swap');
    *{box-sizing:border-box;margin:0;padding:0;}
    html,body{background:${T.bg};}
    ::selection{background:${T.accent};color:${T.bg};}
    ::-webkit-scrollbar{width:5px;}
    ::-webkit-scrollbar-track{background:${T.bg};}
    ::-webkit-scrollbar-thumb{background:${T.accentDim};border-radius:3px;}
    input:focus,select:focus{outline:none;}
    @keyframes pFloat{0%{transform:translateY(0) translateX(0);opacity:0.05;}50%{opacity:0.18;}100%{transform:translateY(-50px) translateX(15px);opacity:0.02;}}
    @keyframes heroGlow{0%,100%{filter:drop-shadow(0 0 20px rgba(0,200,255,0.25));}50%{filter:drop-shadow(0 0 45px rgba(0,200,255,0.55));}}
    @keyframes fadeUp{from{opacity:0;transform:translateY(24px);}to{opacity:1;transform:translateY(0);}}
    @keyframes pulseRing{0%{transform:scale(0.95);opacity:0.4;}50%{transform:scale(1.06);opacity:0.9;}100%{transform:scale(0.95);opacity:0.4;}}
    @keyframes spin{to{transform:rotate(360deg);}}
    @keyframes skullBreathe{0%,100%{transform:scale(1);}50%{transform:scale(1.03);}}
    @keyframes pulse{0%,100%{opacity:0.6;}50%{opacity:1;}}
  `;

  /* ═══════ LANDING ═══════ */
  if (view === "landing") {
    return (<div style={{ minHeight:"100vh",background:T.bg,color:T.text,fontFamily:"'Rajdhani',sans-serif",position:"relative",overflow:"hidden" }}>
      <style>{CSS}</style><Particles />
      <div style={{ position:"absolute",top:"-15%",left:"50%",transform:"translateX(-50%)",width:1000,height:1000,borderRadius:"50%",background:"radial-gradient(circle,rgba(0,200,255,0.05) 0%,rgba(0,100,180,0.015) 40%,transparent 65%)",pointerEvents:"none",zIndex:0 }} />
      <nav style={{ position:"relative",zIndex:10,display:"flex",justifyContent:"space-between",alignItems:"center",padding:"18px 36px",borderBottom:`1px solid ${T.border}` }}>
        <div style={{ display:"flex",alignItems:"center",gap:10 }}>
          <div style={{ width:30,height:30,borderRadius:"50%",background:`radial-gradient(circle at 30% 30%,${T.accent},${T.accentDim})`,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 0 18px ${T.accentGlow}`,fontSize:15 }}>☠</div>
          <span style={{ fontFamily:"'Orbitron',sans-serif",fontSize:12,fontWeight:700,color:T.textBright,letterSpacing:2.5 }}>CLAN ARMOURY</span>
        </div>
        <span style={{ fontSize:10,color:T.textDim,letterSpacing:1.5,cursor:"pointer",padding:"6px 14px",border:`1px solid ${T.border}`,borderRadius:6 }} onClick={() => setView("app")}
          onMouseEnter={e => { e.currentTarget.style.borderColor=T.borderBright; e.currentTarget.style.color=T.accent; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor=T.border; e.currentTarget.style.color=T.textDim; }}>LAUNCH APP →</span>
      </nav>
      <div style={{ position:"relative",zIndex:1,display:"flex",flexDirection:"column",alignItems:"center",textAlign:"center",padding:"90px 24px 50px" }}>
        <div style={{ width:150,height:150,borderRadius:"50%",marginBottom:44,position:"relative",display:"flex",alignItems:"center",justifyContent:"center" }}>
          <div style={{ position:"absolute",inset:-10,borderRadius:"50%",border:`2px solid ${T.accent}`,opacity:0.15,animation:"pulseRing 3.5s ease-in-out infinite" }} />
          <div style={{ position:"absolute",inset:-24,borderRadius:"50%",border:`1px solid ${T.accent}`,opacity:0.06,animation:"pulseRing 3.5s ease-in-out 0.6s infinite" }} />
          <div style={{ position:"absolute",inset:-40,borderRadius:"50%",border:`1px solid ${T.accent}`,opacity:0.03,animation:"pulseRing 3.5s ease-in-out 1.2s infinite" }} />
          <div style={{ width:130,height:130,borderRadius:"50%",background:"radial-gradient(circle at 40% 35%,#1a3a5c,#0c1c30,#060a12)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:60,animation:"heroGlow 3s ease-in-out infinite, skullBreathe 4s ease-in-out infinite",boxShadow:`0 0 80px ${T.accentGlow}, inset 0 0 40px rgba(0,200,255,0.04)`,border:`1px solid ${T.borderBright}` }}>☠</div>
        </div>
        <h1 style={{ fontFamily:"'Orbitron',sans-serif",fontSize:"clamp(32px,5vw,48px)",fontWeight:900,color:T.textBright,letterSpacing:8,lineHeight:1.1,marginBottom:10,animation:"fadeUp 0.8s ease both",textShadow:"0 0 80px rgba(0,200,255,0.15)" }}>CLAN ARMOURY</h1>
        <p style={{ fontSize:13,color:T.accent,letterSpacing:5,textTransform:"uppercase",fontWeight:600,marginBottom:28,animation:"fadeUp 0.8s ease 0.1s both" }}>OSRS Clan Intelligence Platform</p>
        <p style={{ fontSize:14,color:T.textDim,maxWidth:520,lineHeight:1.9,marginBottom:44,animation:"fadeUp 0.8s ease 0.2s both" }}>
          Pull your clan roster from Wise Old Man, then scrape every member's real stats directly from the OSRS HiScores. All 23 skills, combat levels, and XP — exported to a spreadsheet in under 2 minutes.
        </p>
        <div style={{ display:"flex",gap:0,maxWidth:540,width:"100%",animation:"fadeUp 0.8s ease 0.3s both",border:`1px solid ${T.borderBright}`,borderRadius:12,overflow:"hidden",boxShadow:`0 0 50px ${T.accentGlow}, 0 8px 40px rgba(0,0,0,0.5)` }}>
          <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSearch()} placeholder="Enter clan name or WOM Group ID..."
            style={{ flex:1,padding:"18px 22px",fontSize:15,background:"rgba(6,10,18,0.92)",border:"none",color:T.textBright,fontFamily:"'Rajdhani',sans-serif",fontWeight:500,letterSpacing:0.5 }} />
          <button onClick={() => handleSearch()} style={{ padding:"18px 36px",fontSize:12,fontWeight:800,fontFamily:"'Orbitron',sans-serif",letterSpacing:2.5,background:`linear-gradient(135deg,${T.accentDim},${T.accent})`,color:T.bg,border:"none",cursor:"pointer",transition:"all 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.25)"} onMouseLeave={e => e.currentTarget.style.filter = "brightness(1)"}>SCAN</button>
        </div>
      </div>
      <div style={{ position:"relative",zIndex:1,maxWidth:880,margin:"0 auto",padding:"30px 24px 70px",display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:18 }}>
        <FeatureCard icon="⚔️" title="COMBAT ANALYSIS" desc="Real combat levels calculated from actual attack, strength, defence, hitpoints, ranged, prayer, and magic — scraped live from Jagex." />
        <FeatureCard icon="📊" title="FULL SKILL AUDIT" desc="All 23 skills with accurate levels, XP, and hiscores ranks pulled straight from the OSRS HiScores." />
        <FeatureCard icon="📥" title="XLSX EXPORT" desc="Download a multi-sheet spreadsheet with stats, XP, and ranks. Ready for clan leadership review." />
      </div>
      <div style={{ maxWidth:880,margin:"0 auto",padding:"0 24px" }}><div style={{ height:1,background:`linear-gradient(90deg,transparent,${T.border},transparent)` }} /></div>
      <div style={{ position:"relative",zIndex:1,maxWidth:880,margin:"0 auto",padding:"50px 24px 70px",textAlign:"center" }}>
        <p style={{ fontSize:10,color:T.textMuted,letterSpacing:3,textTransform:"uppercase",fontWeight:700,marginBottom:32 }}>How It Works</p>
        <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:40 }}>
          {[{ s:"01",t:"Search",d:"Enter your clan name or WOM Group ID to pull the complete roster" },{ s:"02",t:"Scrape",d:"Each member's stats are fetched directly from the OSRS HiScores — no rate limit bottleneck" },{ s:"03",t:"Export",d:"View live results or download a complete multi-sheet spreadsheet" }].map(x => (
            <div key={x.s}><div style={{ fontSize:32,fontWeight:900,fontFamily:"'Orbitron',sans-serif",color:T.accent,opacity:0.2,marginBottom:10 }}>{x.s}</div>
              <div style={{ fontSize:15,fontWeight:700,color:T.textBright,marginBottom:6 }}>{x.t}</div>
              <div style={{ fontSize:12,color:T.textDim,lineHeight:1.7 }}>{x.d}</div></div>))}
        </div>
      </div>
      <div style={{ borderTop:`1px solid ${T.border}`,padding:"20px 36px",display:"flex",justifyContent:"center",position:"relative",zIndex:1 }}>
        <span style={{ fontSize:9,color:T.textMuted,letterSpacing:2.5 }}>POWERED BY WISE OLD MAN + OSRS HISCORES</span>
      </div>
    </div>);
  }

  /* ═══════ APP VIEW ═══════ */
  return (<div style={{ minHeight:"100vh",background:T.bg,color:T.text,fontFamily:"'Rajdhani',sans-serif",position:"relative" }}>
    <style>{CSS}</style><Particles />

    <div style={{ position:"sticky",top:0,zIndex:50,background:"rgba(6,10,18,0.94)",backdropFilter:"blur(12px)",borderBottom:`1px solid ${T.border}` }}>
      <div style={{ display:"flex",alignItems:"center",gap:16,padding:"10px 24px" }}>
        <div style={{ display:"flex",alignItems:"center",gap:8,cursor:"pointer",flexShrink:0 }} onClick={() => { cancelRef.current=true; setView("landing"); setSelectedGroup(null); setMembers([]); setSearchResults(null); setError(null); setAllDone(false); setTotalCount(0); }}>
          <div style={{ width:24,height:24,borderRadius:"50%",background:`radial-gradient(circle at 30% 30%,${T.accent},${T.accentDim})`,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 0 10px ${T.accentGlow}`,fontSize:12 }}>☠</div>
          <span style={{ fontFamily:"'Orbitron',sans-serif",fontSize:10,fontWeight:700,color:T.textBright,letterSpacing:2 }}>CLAN ARMOURY</span>
        </div>
        <div style={{ flex:1,display:"flex",gap:0,maxWidth:380,marginLeft:16 }}>
          <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSearch()} placeholder="Clan name or Group ID..."
            style={{ flex:1,padding:"8px 14px",fontSize:13,background:"rgba(0,0,0,0.35)",border:`1px solid ${T.border}`,borderRight:"none",borderRadius:"6px 0 0 6px",color:T.textBright,fontFamily:"'Rajdhani',sans-serif",fontWeight:500 }} />
          <button onClick={() => handleSearch()} disabled={initialLoading} style={{ padding:"8px 18px",fontSize:10,fontWeight:800,fontFamily:"'Orbitron',sans-serif",letterSpacing:1.5,background:initialLoading?"rgba(0,200,255,0.08)":`linear-gradient(135deg,${T.accentDim},${T.accent})`,color:initialLoading?T.textDim:T.bg,border:`1px solid ${T.borderBright}`,borderRadius:"0 6px 6px 0",cursor:initialLoading?"wait":"pointer" }}>{initialLoading ? "..." : "SCAN"}</button>
        </div>
      </div>

      {isScanning && (<div style={{ padding:"0 24px 8px" }}>
        <div style={{ display:"flex",alignItems:"center",gap:10 }}>
          <div style={{ flex:1,height:4,background:"rgba(0,200,255,0.06)",borderRadius:2,overflow:"hidden" }}>
            <div style={{ width:`${pct}%`,height:"100%",background:`linear-gradient(90deg,${T.accentDim},${T.accent})`,borderRadius:2,transition:"width 0.3s" }} />
          </div>
          <span style={{ fontSize:10,color:T.accent,fontFamily:"'Orbitron',sans-serif",fontWeight:600,minWidth:100,textAlign:"right",animation:"pulse 1.5s ease-in-out infinite" }}>{processed} / {totalCount} ({pct}%)</span>
        </div>
        <div style={{ fontSize:10,color:T.textDim,marginTop:4,display:"flex",justifyContent:"space-between" }}>
          <span>{statusMsg}</span>
          {etaSec > 0 && <span style={{ color:T.textMuted }}>~{etaSec < 60 ? `${etaSec}s` : `${Math.ceil(etaSec/60)}m`} remaining</span>}
        </div>
      </div>)}
      {allDone && totalCount > 0 && (<div style={{ padding:"0 24px 6px",display:"flex",gap:12 }}>
        <span style={{ fontSize:10,color:T.green,fontWeight:600 }}>✓ {loadedCount} members loaded</span>
        {failedCount > 0 && <span style={{ fontSize:10,color:T.warn,fontWeight:600 }}>⚠ {failedCount} not on hiscores</span>}
      </div>)}
    </div>

    <div style={{ maxWidth:1000,margin:"0 auto",padding:"20px 24px 60px",position:"relative",zIndex:1 }}>
      {initialLoading && members.length === 0 && (
        <div style={{ padding:60,textAlign:"center" }}>
          <div style={{ width:36,height:36,border:`3px solid ${T.border}`,borderTop:`3px solid ${T.accent}`,borderRadius:"50%",margin:"0 auto 20px",animation:"spin 0.7s linear infinite" }} />
          <div style={{ color:T.accent,fontSize:12,fontFamily:"'Orbitron',sans-serif",letterSpacing:2 }}>{statusMsg || "Connecting..."}</div>
        </div>
      )}

      {error && (<div style={{ padding:"14px 18px",background:"rgba(255,60,80,0.06)",border:"1px solid rgba(255,60,80,0.2)",borderRadius:8,color:"#ff6b7a",fontSize:13,marginTop:16 }}>{error}</div>)}

      {searchResults && !initialLoading && (
        <div style={{ marginTop:20 }}>
          <p style={{ fontSize:10,color:T.textMuted,textTransform:"uppercase",letterSpacing:2,marginBottom:12,fontWeight:700 }}>Select a group ({searchResults.length} results)</p>
          {searchResults.map(g => (<div key={g.id} onClick={() => selectGroup(g)} style={{ padding:"14px 18px",marginBottom:4,background:T.bgCard,cursor:"pointer",borderRadius:8,border:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",transition:"all 0.15s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor=T.borderBright; e.currentTarget.style.boxShadow=`0 0 20px ${T.accentGlow}`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor=T.border; e.currentTarget.style.boxShadow="none"; }}>
            <div><span style={{ color:T.textBright,fontWeight:700,fontFamily:"'Orbitron',sans-serif",fontSize:11,letterSpacing:1 }}>{g.name}</span>
              {g.description && <span style={{ color:T.textMuted,fontSize:11,marginLeft:12 }}>{g.description.slice(0, 60)}</span>}</div>
            <div style={{ display:"flex",gap:16,alignItems:"center" }}>
              <span style={{ fontSize:11,color:T.textDim }}>{g.memberCount} members</span>
              <span style={{ fontSize:10,color:T.textMuted,fontFamily:"'Orbitron',sans-serif" }}>#{g.id}</span>
            </div>
          </div>))}
        </div>
      )}

      {selectedGroup && members.length > 0 && (<>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginTop:20,marginBottom:16,flexWrap:"wrap",gap:12 }}>
          <div>
            <h2 style={{ fontFamily:"'Orbitron',sans-serif",fontSize:16,fontWeight:700,color:T.accent,letterSpacing:2.5 }}>{selectedGroup.name}</h2>
            <p style={{ fontSize:11,color:T.textMuted,marginTop:3,letterSpacing:1 }}>{totalCount} members · Group #{selectedGroup.id}{selectedGroup.verified && <span style={{ color:T.green,marginLeft:8 }}>✓ VERIFIED</span>}</p>
          </div>
          <div style={{ display:"flex",gap:8,alignItems:"center",flexWrap:"wrap" }}>
            <input value={filterText} onChange={e => setFilterText(e.target.value)} placeholder="Filter..." style={{ padding:"7px 12px",fontSize:12,width:140,background:"rgba(0,0,0,0.3)",border:`1px solid ${T.border}`,borderRadius:6,color:T.textBright,fontFamily:"'Rajdhani',sans-serif" }} />
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ padding:"7px 12px",fontSize:12,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:6,color:T.accent,cursor:"pointer",fontFamily:"'Rajdhani',sans-serif",fontWeight:600 }}>
              <option value="combat">Sort: Combat</option><option value="total">Sort: Total</option><option value="xp">Sort: XP</option><option value="name">Sort: Name</option>
            </select>
            <button onClick={() => exportToXlsx(sorted.filter(m => m.loaded && !m.failed), selectedGroup.name)}
              style={{ padding:"8px 22px",fontSize:11,fontWeight:800,fontFamily:"'Orbitron',sans-serif",letterSpacing:1,background:allDone?`linear-gradient(135deg,#00885a,${T.green})`:"linear-gradient(135deg,#556,#889)",color:allDone?T.bg:"#fff",border:"none",borderRadius:6,cursor:"pointer",boxShadow:allDone?"0 0 20px rgba(0,232,138,0.12)":"none",transition:"all 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.filter="brightness(1.2)"} onMouseLeave={e => e.currentTarget.style.filter="brightness(1)"}>↓ EXPORT{allDone ? "" : " (partial)"}</button>
          </div>
        </div>

        <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:8,marginBottom:20,padding:16,background:T.panel,borderRadius:10,border:`1px solid ${T.border}` }}>
          {(() => {
            const ld = members.filter(m => m.loaded && !m.failed);
            const combats = ld.map(m => calcCombatLevel(m.data?.skills || {}));
            const totals = ld.map(m => m.data?.skills?.overall?.level ?? 0);
            const avg = a => a.length ? Math.round(a.reduce((x, y) => x + y, 0) / a.length) : 0;
            const mx = a => a.length ? Math.max(...a) : 0;
            const maxed = ld.filter(m => (m.data?.skills?.overall?.level ?? 0) >= 2277).length;
            return (<>
              <StatBadge label="Avg Combat" value={combats.length ? avg(combats) : "..."} />
              <StatBadge label="Max Combat" value={combats.length ? mx(combats) : "..."} />
              <StatBadge label="Avg Total" value={totals.length ? avg(totals) : "..."} />
              <StatBadge label="Max Total" value={totals.length ? mx(totals) : "..."} />
              <StatBadge label="Maxed" value={maxed} color={T.green} />
              <StatBadge label="Scanned" value={`${processed}/${totalCount}`} color={allDone ? T.green : T.warn} />
            </>);
          })()}
        </div>

        <div style={{ display:"grid",gridTemplateColumns:"36px 1.4fr 0.9fr 0.7fr 0.7fr 1fr",padding:"8px 16px",borderBottom:`1px solid ${T.borderBright}`,position:"sticky",top:isScanning?90:allDone&&totalCount>0?68:48,zIndex:10,background:"rgba(6,10,18,0.96)",backdropFilter:"blur(8px)" }}>
          {["#","PLAYER","COMBAT","STYLE","TOTAL","OVERALL XP"].map((h, i) => (
            <span key={h} style={{ fontSize:9,color:T.textMuted,textTransform:"uppercase",letterSpacing:2,fontWeight:700,textAlign:i>=2&&i<5?"center":i===5?"right":"left" }}>{h}</span>
          ))}
        </div>
        <div>{sorted.map((m, i) => (<PlayerRow key={m.player?.id ?? m.player?.username ?? i} member={m} index={i} expanded={expandedIdx === i} onToggle={() => setExpandedIdx(expandedIdx === i ? null : i)} />))}</div>
        {sorted.length === 0 && filterText && (<div style={{ padding:40,textAlign:"center",color:T.textMuted,fontSize:13 }}>No members match "{filterText}"</div>)}
      </>)}

      {!initialLoading && !searchResults && members.length === 0 && !error && (
        <div style={{ padding:"100px 20px",textAlign:"center" }}>
          <div style={{ fontSize:56,marginBottom:20,opacity:0.08,color:T.accent }}>☠</div>
          <p style={{ color:T.textMuted,fontSize:13,maxWidth:380,margin:"0 auto",lineHeight:1.8 }}>Enter a clan name or Wise Old Man Group ID above to begin scanning.</p>
        </div>
      )}
    </div>
  </div>);
}
